import { ECurrency } from '@ringana/configs';
import { IMerchandiseItem } from '@/common/interfaces/merchandise.interface';
import { IPatchCartItemRequestData } from '@/common/interfaces/request.interface';
import {
  IShoppingCart,
  IShoppingCartIndividual,
  IShoppingCartItem,
} from '@/common/interfaces/cart.interface';
import DomEventHelper from '@/helpers/DomEventHelper';
import LocalStorageHelper from './LocalStorageHelper';
import config from '../../config';

export default class CartHelper {
  public static getMaxAvailableForCart(cartId: number, matchcode: string, maxPerCart: number): number {
    let internalMaxPerCart: number | undefined = maxPerCart;
    const cartsInStorage = LocalStorageHelper.getCart();
    let amountInCart = 0;
    if(cartsInStorage?.shoppingCarts) {
      let cart = cartsInStorage.shoppingCarts.find(shoppingCart => shoppingCart.cartId === cartId);
      if(!cart) {
        cart = cartsInStorage.shoppingCarts.find(shoppingCart => shoppingCart.name === `myOwnCart`);
        if(!cart) {
          return 0;
        }
      }
      const items = cart.items.filter(item => item.matchcode === matchcode);
      for(const item of items) {
        amountInCart = item.amount;
        internalMaxPerCart = item.maxPerCart ?? maxPerCart;
      }
    }
    return internalMaxPerCart ? internalMaxPerCart - amountInCart : 0;
  }

  public static updateCartCount(count: number): void {
    LocalStorageHelper.setCartItemsCount(count);
    DomEventHelper.fireEvent({ name: `rin-cart-update` });
  }

  public static updateLocalCart(updatedShoppingCart: IShoppingCartIndividual): void {
    let cart: Partial<IShoppingCart> | null = LocalStorageHelper.getCart();
    if(!cart) {
      /*
       * this case should only really ever happen when a non logged-in user visits the .com directly
       * on a product category page and adds a product to the cart
       *
       * provide a default cart with the key/value pairs we need for this function
       */
      cart = {
        shoppingCarts: [],
        totalItemsCount: 0,
      };
    }
    cart.shoppingCarts = cart.shoppingCarts?.filter((shoppingCart: IShoppingCartIndividual): boolean => shoppingCart.cartId !== updatedShoppingCart.cartId) ?? [];
    cart.shoppingCarts.push(updatedShoppingCart);
    let totalItemsInAllCarts = 0;
    for(const cartItem of cart.shoppingCarts) {
      totalItemsInAllCarts += cartItem.itemsCount;
    }
    cart.totalItemsCount = totalItemsInAllCarts;
    /*
     * this case should only really ever happen when a non logged-in user visits the .com directly
     * on a product category page and adds a product to the cart
     *
     * cast needed to get the Partial<IShoppingCart> set as the setCart function expects a IShoppingCart
     */
    LocalStorageHelper.setCart(cart as IShoppingCart);
    CartHelper.updateCartCount(totalItemsInAllCarts);
  }

  public static getEmptyCart(): IShoppingCart {
    return {
      currency: ECurrency.EUR,
      deliveryAddress: {
        city: ``,
        deliveryDefault: false,
        receiptDefault: false,
        recipient: ``,
        street: ``,
        streetNumber: ``,
        zipCode: ``,
      },
      deliveryType: {
        cost: 0,
        id: -1,
        name: ``,
      },
      shoppingCarts: [
        {
          availableSets: [],
          cartId: -1,
          cartTypeId: config.cartTypes.EIGENBEDARF,
          cn: `-1`,
          coupons: [],
          deliveryAddress: {
            city: ``,
            deliveryDefault: false,
            receiptDefault: false,
            recipient: ``,
            street: ``,
            streetNumber: ``,
            zipCode: ``,
          },
          deliveryTypesInfo: ``,
          invoiceAddress: {
            city: ``,
            deliveryDefault: false,
            receiptDefault: false,
            recipient: ``,
            street: ``,
            streetNumber: ``,
            zipCode: ``,
          },
          isSingleShipmentCart: false,
          items: [],
          itemsCount: 0,
          name: ``,
          partner: false,
          paymentType: ``,
          specialOffers: [],
          specialOffersInfo: ``,
          sum: {
            open: 0,
            points: 0,
            postage: 0,
            subtotal: 0,
            total: 0,
            vat: 0,
          },
          supplements: [],
          taxNumber: ``,
          taxNumberStatus: ``,
        },
      ],
      sum: {
        open: 0,
        points: 0,
        postage: 0,
        subtotal: 0,
        total: 0,
        vat: 0,
      },
      totalItemsCount: 0,
    };
  }

  public static preparePatchCartItemRequestData(item: IShoppingCartItem & IMerchandiseItem): IPatchCartItemRequestData {
    return {
      amount: item.amount,
      cartContentId: item.cartContentId,
      matchcode: item.matchcode,
      mutation0: item.mutations[0]?.value,
      mutation1: item.mutations[1]?.value,
      mutation2: item.mutations[2]?.value,
      mutation3: item.mutations[3]?.value,
      mutation4: item.mutations[4]?.value,
      mutation5: item.mutations[5]?.value,
      mutation6: item.mutations[6]?.value,
      mutation7: item.mutations[7]?.value,
      mutation8: item.mutations[8]?.value,
      mutation9: item.mutations[9]?.value,
    };
  }
}
